import { music24LocaleOf, type Music24Locale } from './music24Locales';

/**
 * The words of the Release Kit images and videos in the 9 Music24 languages (owner request
 * 2026-10-29: "Out now", "7 days to go", "Link in bio" in the language of the artist). One table
 * for the server render (music24-api, the kit builders of `story/`) and the web Studio preview, so
 * both draw the same words. It has no react-intl and no other import than `music24Locales.ts`, so
 * every project may read it, also at module load.
 *
 * The language of a kit is the language setting of the workspace owner (Settings → Account,
 * `UserModel.music24Locale`); "Automatic" (null) and an unknown value give English
 * (`kitTextLocaleOf`). The kit stores the language that its texts used in the plan
 * (`plan.textLocale`), so a date redo writes the texts again in the same language.
 *
 * Rules of the copy: short (a pill holds about 16 characters), the marketing tone of the app copy,
 * the pre-save word as each market uses it. A date is a label of the locale ("16 Oct", "16. Okt.",
 * "16 paź"); a weekday phrase is a whole entry per weekday, because the grammar of "Out Friday"
 * differs per language (Polish "w piątek", Portuguese "na sexta").
 */

/** A language of the kit texts: one of the 9 Music24 locales. */
export type KitTextLocale = Music24Locale;

/** The language of a kit when nobody chose one. */
export const DEFAULT_KIT_TEXT_LOCALE: KitTextLocale = 'en';

/** Seven entries, Sunday first (the index of `Date.getUTCDay`). */
type Week = readonly [string, string, string, string, string, string, string];
/** Twelve entries, January first. */
type Year = readonly [string, string, string, string, string, string, string, string, string, string, string, string];

/** The words of one language. Every key is a fixed text or a pure function of a label. */
export interface KitWords {
  /** The kicker of a pre-save Story, a link preview and an announce post. */
  newSingle: string;
  /** The hero of an out-now Story; the banner status and the AI reel chip from the release day on. */
  outNow: string;
  /** The pill before the release when the song has a published pre-save page. */
  preSaveNow: string;
  /** The pill from the release day on. */
  listenNow: string;
  /** The pill before the release with no published pre-save page; the SmartLink CTA with no date. */
  comingSoon: string;
  /** The words before the SmartLink address of a feed post and an AI reel. */
  linkInBio: string;
  /** The words before the SmartLink address of a banner. */
  listenOn: string;
  /** The word that takes the place of a date in a caption when the song has no release date now (the date redo). */
  soonWord: string;
  /** The line under the day number of a countdown (3 or 7 days). */
  daysToGo: string;
  /** The line under the day number of a countdown one day before. */
  dayToGo: string;
  /** "Out 16 Oct", "Out Fri 16 Oct": the release before the release day. */
  outDate: (label: string) => string;
  /** "Tomorrow · 16 Oct": the date ring of a countdown one day before. */
  tomorrowDate: (label: string) => string;
  /** The end card of a countdown video one day before. */
  outTomorrow: string;
  /** "Out Friday" by weekday (Sunday first): the end card of a countdown video, the AI reel chip. */
  outOnWeekday: Week;
  /** The link preview badge from the release day on: the small top line and the large value. */
  badgeOutTop: string;
  badgeOutValue: string;
  /** The link preview badge before the release day: the small top line over the date. */
  badgeBeforeTop: string;
  /** The link preview badge with no date: the small top line and the large value. */
  badgeComingTop: string;
  badgeComingValue: string;
  /** The YouTube thumbnail labels. */
  officialAudio: string;
  lyricVideo: string;
  visualizer: string;
  officialVideo: string;
  /** The month as a date label writes it (`dateLabel`), January first. */
  monthsShort: Year;
  /** The month as a date in running text writes it (Polish: the genitive), January first. */
  monthsLong: Year;
  /** The short weekday of a day label (`dayLabel`), Sunday first. */
  weekdaysShort: Week;
  /** The weekday standing alone (the link preview badge), Sunday first, as written in a sentence. */
  weekdaysLong: Week;
  /** "16 Oct" for day 16 of month 9 (0-based month). */
  dateLabel: (day: number, month: number) => string;
  /** "Fri 16 Oct" for weekday 5, day 16, month 9 (0-based weekday and month). */
  dayLabel: (weekday: number, day: number, month: number) => string;
}

const pick = <T extends readonly string[]>(list: T, index: number): string => list[index] ?? '';

const EN_MONTHS: Year = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EN_WEEKDAYS: Week = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EN: KitWords = {
  newSingle: 'New single',
  outNow: 'Out now',
  preSaveNow: 'Pre-save now',
  listenNow: 'Listen now',
  comingSoon: 'Coming soon',
  linkInBio: 'Link in bio',
  listenOn: 'Listen on',
  soonWord: 'soon',
  daysToGo: 'days to go',
  dayToGo: 'day to go',
  outDate: (label) => `Out ${label}`,
  tomorrowDate: (label) => `Tomorrow · ${label}`,
  outTomorrow: 'Out tomorrow',
  outOnWeekday: [
    'Out Sunday',
    'Out Monday',
    'Out Tuesday',
    'Out Wednesday',
    'Out Thursday',
    'Out Friday',
    'Out Saturday',
  ],
  badgeOutTop: 'Out',
  badgeOutValue: 'Now',
  badgeBeforeTop: 'Out',
  badgeComingTop: 'Coming',
  badgeComingValue: 'Soon',
  officialAudio: 'Official audio',
  lyricVideo: 'Lyric video',
  visualizer: 'Visualizer',
  officialVideo: 'Official video',
  monthsShort: EN_MONTHS,
  monthsLong: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  weekdaysShort: EN_WEEKDAYS,
  weekdaysLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ...labels(EN_MONTHS, EN_WEEKDAYS, {}),
};

/** A label builder: "<weekday> <day> <month>" or "<day> <month>" with the lists of one language. */
function labels(
  months: Year,
  weekdays: Week,
  form: { dayDot?: boolean; weekdayComma?: boolean }
): Pick<KitWords, 'dateLabel' | 'dayLabel'> {
  const date = (day: number, month: number): string =>
    `${day}${form.dayDot === true ? '.' : ''} ${pick(months, month)}`;
  return {
    dateLabel: date,
    dayLabel: (weekday, day, month) =>
      `${pick(weekdays, weekday)}${form.weekdayComma === true ? ',' : ''} ${date(day, month)}`,
  };
}

const DE_MONTHS: Year = [
  'Jan.',
  'Feb.',
  'März',
  'Apr.',
  'Mai',
  'Juni',
  'Juli',
  'Aug.',
  'Sept.',
  'Okt.',
  'Nov.',
  'Dez.',
];
const DE_WEEKDAYS: Week = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
const DE_WEEKDAYS_LONG: Week = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const DE: KitWords = {
  newSingle: 'Neue Single',
  outNow: 'Jetzt erhältlich',
  preSaveNow: 'Jetzt pre-saven',
  listenNow: 'Jetzt anhören',
  comingSoon: 'Demnächst',
  linkInBio: 'Link in der Bio',
  listenOn: 'Anhören auf',
  soonWord: 'bald',
  daysToGo: 'Tage noch',
  dayToGo: 'Tag noch',
  outDate: (label) => `Ab ${label}`,
  tomorrowDate: (label) => `Morgen · ${label}`,
  outTomorrow: 'Ab morgen',
  outOnWeekday: ['Ab Sonntag', 'Ab Montag', 'Ab Dienstag', 'Ab Mittwoch', 'Ab Donnerstag', 'Ab Freitag', 'Ab Samstag'],
  badgeOutTop: 'Ab',
  badgeOutValue: 'Jetzt',
  badgeBeforeTop: 'Ab',
  badgeComingTop: 'Kommt',
  badgeComingValue: 'Bald',
  officialAudio: 'Offizielles Audio',
  lyricVideo: 'Lyric-Video',
  visualizer: 'Visualizer',
  officialVideo: 'Offizielles Video',
  monthsShort: DE_MONTHS,
  monthsLong: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  weekdaysShort: DE_WEEKDAYS,
  weekdaysLong: DE_WEEKDAYS_LONG,
  ...labels(DE_MONTHS, DE_WEEKDAYS, { dayDot: true, weekdayComma: true }),
};

const ES_MONTHS: Year = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const ES_WEEKDAYS: Week = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

const ES: KitWords = {
  newSingle: 'Nuevo sencillo',
  outNow: 'Ya disponible',
  preSaveNow: 'Haz pre-save ya',
  listenNow: 'Escúchala ya',
  comingSoon: 'Próximamente',
  linkInBio: 'Link en la bio',
  listenOn: 'Escúchala en',
  soonWord: 'pronto',
  daysToGo: 'días para el estreno',
  dayToGo: 'día para el estreno',
  outDate: (label) => `Sale el ${label}`,
  tomorrowDate: (label) => `Mañana · ${label}`,
  outTomorrow: 'Sale mañana',
  outOnWeekday: [
    'Sale el domingo',
    'Sale el lunes',
    'Sale el martes',
    'Sale el miércoles',
    'Sale el jueves',
    'Sale el viernes',
    'Sale el sábado',
  ],
  badgeOutTop: 'Ya',
  badgeOutValue: 'Salió',
  badgeBeforeTop: 'Sale el',
  badgeComingTop: 'Muy',
  badgeComingValue: 'Pronto',
  officialAudio: 'Audio oficial',
  lyricVideo: 'Video con letra',
  visualizer: 'Visualizer',
  officialVideo: 'Video oficial',
  monthsShort: ES_MONTHS,
  monthsLong: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  weekdaysShort: ES_WEEKDAYS,
  weekdaysLong: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  ...labels(ES_MONTHS, ES_WEEKDAYS, {}),
};

const FR_MONTHS: Year = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];
const FR_WEEKDAYS: Week = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

const FR: KitWords = {
  newSingle: 'Nouveau single',
  outNow: 'Disponible',
  preSaveNow: 'Pre-save maintenant',
  listenNow: 'Écoute maintenant',
  comingSoon: 'Bientôt',
  linkInBio: 'Lien en bio',
  listenOn: 'Écoute sur',
  soonWord: 'bientôt',
  daysToGo: 'jours avant la sortie',
  dayToGo: 'jour avant la sortie',
  outDate: (label) => `Sortie le ${label}`,
  tomorrowDate: (label) => `Demain · ${label}`,
  outTomorrow: 'Sortie demain',
  outOnWeekday: [
    'Sortie dimanche',
    'Sortie lundi',
    'Sortie mardi',
    'Sortie mercredi',
    'Sortie jeudi',
    'Sortie vendredi',
    'Sortie samedi',
  ],
  badgeOutTop: 'Déjà',
  badgeOutValue: 'Dispo',
  badgeBeforeTop: 'Sortie le',
  badgeComingTop: 'Très',
  badgeComingValue: 'Bientôt',
  officialAudio: 'Audio officiel',
  lyricVideo: 'Vidéo lyrics',
  visualizer: 'Visualizer',
  officialVideo: 'Clip officiel',
  monthsShort: FR_MONTHS,
  monthsLong: [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
  ],
  weekdaysShort: FR_WEEKDAYS,
  weekdaysLong: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
  ...labels(FR_MONTHS, FR_WEEKDAYS, {}),
};

const IT_MONTHS: Year = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const IT_WEEKDAYS: Week = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];

const IT: KitWords = {
  newSingle: 'Nuovo singolo',
  outNow: 'Fuori ora',
  preSaveNow: 'Fai il pre-save',
  listenNow: 'Ascolta ora',
  comingSoon: 'In arrivo',
  linkInBio: 'Link in bio',
  listenOn: 'Ascolta su',
  soonWord: 'presto',
  daysToGo: "giorni all'uscita",
  dayToGo: "giorno all'uscita",
  outDate: (label) => `Esce il ${label}`,
  tomorrowDate: (label) => `Domani · ${label}`,
  outTomorrow: 'Esce domani',
  outOnWeekday: [
    'Esce domenica',
    'Esce lunedì',
    'Esce martedì',
    'Esce mercoledì',
    'Esce giovedì',
    'Esce venerdì',
    'Esce sabato',
  ],
  badgeOutTop: 'Fuori',
  badgeOutValue: 'Ora',
  badgeBeforeTop: 'Esce il',
  badgeComingTop: 'In',
  badgeComingValue: 'Arrivo',
  officialAudio: 'Audio ufficiale',
  lyricVideo: 'Video con testo',
  visualizer: 'Visualizer',
  officialVideo: 'Video ufficiale',
  monthsShort: IT_MONTHS,
  monthsLong: [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
  ],
  weekdaysShort: IT_WEEKDAYS,
  weekdaysLong: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
  ...labels(IT_MONTHS, IT_WEEKDAYS, {}),
};

const NL_MONTHS: Year = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const NL_WEEKDAYS: Week = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

const NL: KitWords = {
  newSingle: 'Nieuwe single',
  outNow: 'Nu uit',
  preSaveNow: 'Pre-save nu',
  listenNow: 'Luister nu',
  comingSoon: 'Binnenkort',
  linkInBio: 'Link in bio',
  listenOn: 'Luister op',
  soonWord: 'binnenkort',
  daysToGo: 'dagen te gaan',
  dayToGo: 'dag te gaan',
  outDate: (label) => `Uit op ${label}`,
  tomorrowDate: (label) => `Morgen · ${label}`,
  outTomorrow: 'Morgen uit',
  outOnWeekday: [
    'Uit op zondag',
    'Uit op maandag',
    'Uit op dinsdag',
    'Uit op woensdag',
    'Uit op donderdag',
    'Uit op vrijdag',
    'Uit op zaterdag',
  ],
  badgeOutTop: 'Nu',
  badgeOutValue: 'Uit',
  badgeBeforeTop: 'Uit op',
  badgeComingTop: 'Komt',
  badgeComingValue: 'Binnenkort',
  officialAudio: 'Officiële audio',
  lyricVideo: 'Lyricvideo',
  visualizer: 'Visualizer',
  officialVideo: 'Officiële video',
  monthsShort: NL_MONTHS,
  monthsLong: [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ],
  weekdaysShort: NL_WEEKDAYS,
  weekdaysLong: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
  ...labels(NL_MONTHS, NL_WEEKDAYS, {}),
};

const PL_MONTHS: Year = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
const PL_WEEKDAYS: Week = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'];

const PL: KitWords = {
  newSingle: 'Nowy singiel',
  outNow: 'Już dostępne',
  preSaveNow: 'Zrób pre-save',
  listenNow: 'Słuchaj teraz',
  comingSoon: 'Już wkrótce',
  linkInBio: 'Link w bio',
  listenOn: 'Słuchaj na',
  soonWord: 'wkrótce',
  daysToGo: 'dni do premiery',
  dayToGo: 'dzień do premiery',
  outDate: (label) => `Premiera ${label}`,
  tomorrowDate: (label) => `Jutro · ${label}`,
  outTomorrow: 'Premiera jutro',
  outOnWeekday: [
    'Premiera w niedzielę',
    'Premiera w poniedziałek',
    'Premiera we wtorek',
    'Premiera w środę',
    'Premiera w czwartek',
    'Premiera w piątek',
    'Premiera w sobotę',
  ],
  badgeOutTop: 'Już',
  badgeOutValue: 'Jest',
  badgeBeforeTop: 'Premiera',
  badgeComingTop: 'Już',
  badgeComingValue: 'Wkrótce',
  officialAudio: 'Oficjalne audio',
  lyricVideo: 'Teledysk z tekstem',
  visualizer: 'Wizualizer',
  officialVideo: 'Oficjalny teledysk',
  monthsShort: PL_MONTHS,
  monthsLong: [
    'stycznia',
    'lutego',
    'marca',
    'kwietnia',
    'maja',
    'czerwca',
    'lipca',
    'sierpnia',
    'września',
    'października',
    'listopada',
    'grudnia',
  ],
  weekdaysShort: PL_WEEKDAYS,
  weekdaysLong: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
  ...labels(PL_MONTHS, PL_WEEKDAYS, {}),
};

const PT_MONTHS: Year = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const PT_WEEKDAYS: Week = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const PT: KitWords = {
  newSingle: 'Novo single',
  outNow: 'Já disponível',
  preSaveNow: 'Faça o pre-save',
  listenNow: 'Ouça agora',
  comingSoon: 'Em breve',
  linkInBio: 'Link na bio',
  listenOn: 'Ouça no',
  soonWord: 'em breve',
  daysToGo: 'dias para o lançamento',
  dayToGo: 'dia para o lançamento',
  outDate: (label) => `Sai em ${label}`,
  tomorrowDate: (label) => `Amanhã · ${label}`,
  outTomorrow: 'Sai amanhã',
  outOnWeekday: [
    'Sai no domingo',
    'Sai na segunda',
    'Sai na terça',
    'Sai na quarta',
    'Sai na quinta',
    'Sai na sexta',
    'Sai no sábado',
  ],
  badgeOutTop: 'Já',
  badgeOutValue: 'Saiu',
  badgeBeforeTop: 'Sai em',
  badgeComingTop: 'Em',
  badgeComingValue: 'Breve',
  officialAudio: 'Áudio oficial',
  lyricVideo: 'Vídeo com letra',
  visualizer: 'Visualizer',
  officialVideo: 'Vídeo oficial',
  monthsShort: PT_MONTHS,
  monthsLong: [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ],
  weekdaysShort: PT_WEEKDAYS,
  weekdaysLong: ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'],
  ...labels(PT_MONTHS, PT_WEEKDAYS, {}),
};

const SV_MONTHS: Year = ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
const SV_WEEKDAYS: Week = ['sön', 'mån', 'tis', 'ons', 'tor', 'fre', 'lör'];

const SV: KitWords = {
  newSingle: 'Ny singel',
  outNow: 'Ute nu',
  preSaveNow: 'Pre-save nu',
  listenNow: 'Lyssna nu',
  comingSoon: 'Kommer snart',
  linkInBio: 'Länk i bion',
  listenOn: 'Lyssna på',
  soonWord: 'snart',
  daysToGo: 'dagar kvar',
  dayToGo: 'dag kvar',
  outDate: (label) => `Ute ${label}`,
  tomorrowDate: (label) => `I morgon · ${label}`,
  outTomorrow: 'Ute i morgon',
  outOnWeekday: [
    'Ute på söndag',
    'Ute på måndag',
    'Ute på tisdag',
    'Ute på onsdag',
    'Ute på torsdag',
    'Ute på fredag',
    'Ute på lördag',
  ],
  badgeOutTop: 'Ute',
  badgeOutValue: 'Nu',
  badgeBeforeTop: 'Ute',
  badgeComingTop: 'Kommer',
  badgeComingValue: 'Snart',
  officialAudio: 'Officiellt ljud',
  lyricVideo: 'Lyrikvideo',
  visualizer: 'Visualizer',
  officialVideo: 'Officiell video',
  monthsShort: SV_MONTHS,
  monthsLong: [
    'januari',
    'februari',
    'mars',
    'april',
    'maj',
    'juni',
    'juli',
    'augusti',
    'september',
    'oktober',
    'november',
    'december',
  ],
  weekdaysShort: SV_WEEKDAYS,
  weekdaysLong: ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag'],
  ...labels(SV_MONTHS, SV_WEEKDAYS, {}),
};

/**
 * The other weekday forms of a caption per language, each a list Sunday first. The date redo reads
 * them and writes the new weekday in the same form. A form can hold its preposition, because the
 * preposition can change with the weekday (Polish "w sobotę" / "we wtorek", Portuguese "na sexta" /
 * "no sábado"). A language with no entry has no other common form: its weekday of a caption is
 * `weekdaysLong` ("Freitag", "el viernes", "vendredi", "venerdì", "vrijdag", "fredag").
 */
export const KIT_WEEKDAY_CAPTION_FORMS: Readonly<Partial<Record<KitTextLocale, ReadonlyArray<Week>>>> = {
  pl: [
    // The accusative: "Premiera w sobotę 17 października".
    ['w niedzielę', 'w poniedziałek', 'we wtorek', 'w środę', 'w czwartek', 'w piątek', 'w sobotę'],
    ['niedzielę', 'poniedziałek', 'wtorek', 'środę', 'czwartek', 'piątek', 'sobotę'],
  ],
  pt: [
    // "lança na sexta, 16 de outubro": the short spoken form, with and without "na" / "no".
    [
      'no domingo',
      'na segunda-feira',
      'na terça-feira',
      'na quarta-feira',
      'na quinta-feira',
      'na sexta-feira',
      'no sábado',
    ],
    ['no domingo', 'na segunda', 'na terça', 'na quarta', 'na quinta', 'na sexta', 'no sábado'],
    ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'],
  ],
};

/** The words of every language. A test checks that each language has every key, with text. */
export const KIT_TEXT: Readonly<Record<KitTextLocale, KitWords>> = {
  en: EN,
  de: DE,
  es: ES,
  fr: FR,
  it: IT,
  nl: NL,
  pl: PL,
  pt: PT,
  sv: SV,
};

/**
 * The language of the kit texts for a stored setting (`UserModel.music24Locale`, a plan's
 * `textLocale`, an answer field): a Music24 locale ("pl", "pt-BR" → "pt"), else English. Pure.
 */
export function kitTextLocaleOf(value: string | null | undefined): KitTextLocale {
  return music24LocaleOf(value) ?? DEFAULT_KIT_TEXT_LOCALE;
}

/** The words of a language; absent: English. Pure. */
export function kitWords(locale?: KitTextLocale | null): KitWords {
  return KIT_TEXT[locale ?? DEFAULT_KIT_TEXT_LOCALE] ?? EN;
}

/** The year, the 0-based month, the day and the 0-based weekday of a `YYYY-MM-DD` date, or null. Pure. */
export function kitDateParts(date: string | null): { month: number; day: number; weekday: number } | null {
  const match = date === null ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (match === null) {
    return null;
  }
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (month < 0 || month > 11 || day < 1 || day > 31) {
    return null;
  }
  const weekday = new Date(Date.UTC(Number(match[1]), month, day)).getUTCDay();
  return { month, day, weekday };
}

/** The first letter in upper case (a weekday that stands alone on a badge). Pure. */
export function capitalizeKitWord(word: string): string {
  return word === '' ? word : `${word.charAt(0).toLocaleUpperCase()}${word.slice(1)}`;
}

/** The name of the language in English (the director prompt: "Write the caption in Polish"). */
export const KIT_TEXT_LANGUAGE_NAMES: Readonly<Record<KitTextLocale, string>> = {
  en: 'English',
  de: 'German',
  es: 'Spanish',
  fr: 'French',
  it: 'Italian',
  nl: 'Dutch',
  pl: 'Polish',
  pt: 'Brazilian Portuguese',
  sv: 'Swedish',
};
